import Vue from 'vue';
import Vuex from 'vuex';
import _ from 'lodash';
import { EventBus } from '@/event-bus';
import {
  serviceCategories,
  countries,
  regions,
} from '@/api/data';
import {
  register,
  requestConfirmationCode,
  checkCode,
  verify,
  suggest,
  verifyMobileId,
  duplicateGoogleSheet,
} from '@/api/signup';
import {
  SET_STATE,
  SET_SERVICE_CATEGORIES,
  SET_COUNTRIES,
  SET_REGIONS,
  SET_COMPANIES,
  SET_STEP,
} from './mutation-types';

Vue.use(Vuex);

const GOOGLE_SHEETS_WEB_APP_URL = 'https://script.google.com/macros/s/AKfycbxTLQXPUCPrfxplBPtYbRFD7WdHOA5mFLw3vztaJzXWtEGn4ulmpIupRie9jNG4mXAiNw/exec';

const safeString = (value) => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }

  return String(value);
};

const safeJson = (value) => {
  try {
    return JSON.stringify(value || {});
  } catch (error) {
    return '';
  }
};

const splitFullName = (fullName) => {
  const parts = safeString(fullName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const [
    lastName = '',
    firstName = '',
    ...middleNameParts
  ] = parts;

  return {
    lastName,
    firstName,
    middleName: middleNameParts.join(' '),
  };
};

const getPageMetadata = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      page_url: '',
      user_agent: '',
      referrer: '',
      first_referrer: '',
      first_landing_url: '',
    };
  }

  return {
    page_url: window.location.href || '',
    user_agent: _.get(window, 'navigator.userAgent') || '',
    referrer: document.referrer || '',
    first_referrer: window.localStorage
      ? window.localStorage.getItem('iwaly_first_referrer') || ''
      : '',
    first_landing_url: window.localStorage
      ? window.localStorage.getItem('iwaly_first_landing_url') || ''
      : '',
  };
};

const getSanitizedRawPayload = (payload) => {
  const raw = _.cloneDeep(payload || {});

  delete raw.password;
  delete raw.code;

  return raw;
};

const getSelectedServiceCategories = (state) => {
  const selectedIds = _.get(state, 'payload.serviceCategories') || [];
  const allCategories = _.get(state, 'serviceCategories') || [];

  return selectedIds.map((id) => {
    const category = allCategories.find(
      (item) => String(item.id) === String(id),
    );

    return {
      id,
      name: _.get(category, 'name') || '',
      code: _.get(category, 'code')
        || _.get(category, 'slug')
        || _.get(category, 'alias')
        || safeString(id),
    };
  });
};

const isVolunteerServiceCategory = (category) => {
  const blob = [
    _.get(category, 'id'),
    _.get(category, 'code'),
    _.get(category, 'slug'),
    _.get(category, 'alias'),
    _.get(category, 'name'),
  ].join(' ').toLowerCase();

  return String(_.get(category, 'id')) === '10'
    || blob.indexOf('volunteer') !== -1
    || blob.indexOf('волонт') !== -1;
};

const getVolunteerSelectionMeta = (selectedCategories) => {
  const selected = selectedCategories || [];
  const volunteerSelected = selected.some(isVolunteerServiceCategory);

  return {
    volunteerSelected,
    volunteerOnly: selected.length > 0 && selected.every(isVolunteerServiceCategory),
  };
};

const buildGoogleSheetPayload = (payload, state) => {
  const selectedCategories = getSelectedServiceCategories(state);
  const metadata = getPageMetadata();

  const legalType = _.get(payload, 'legalType') || {};
  const country = _.get(payload, 'country') || {};
  const region = _.get(payload, 'region') || {};
  const city = _.get(payload, 'city') || {};

  const serviceCodes = selectedCategories
    .map((item) => item.code)
    .filter(Boolean);

  const serviceNames = selectedCategories
    .map((item) => item.name)
    .filter(Boolean);

  const serviceIds = selectedCategories
    .map((item) => item.id)
    .filter(Boolean);

  const volunteerMeta = getVolunteerSelectionMeta(selectedCategories);
  const demandStatus = _.get(payload, 'contractorDemandStatus') || null;
  const demandOverall = _.get(demandStatus, 'overall') || {};

  const {
    lastName,
    firstName,
    middleName,
  } = splitFullName(_.get(payload, 'name') || '');

  const regionName = _.get(region, 'name') || '';
  const regionId = _.get(region, 'id') || '';
  const cityName = _.get(city, 'name') || _.get(payload, 'cityName') || '';
  const cityId = _.get(city, 'id') || '';

  const comment = _.get(payload, 'comment') || '';

  return {
    source: 'original_signup',
    lead_type: 'contractor_registration',
    sheet_name: 'Регистрация исполнителей',

    last_name: lastName,
    first_name: firstName,
    middle_name: middleName,

    phone: _.get(payload, 'phone') || '',
    phone_e164: _.get(payload, 'phone') || '',
    email: _.get(payload, 'email') || '',

    work_region: regionName,
    work_region_id: regionId,
    work_region_name: regionName,

    city: cityName,
    city_id: cityId,
    city_name: cityName,

    service_items: serviceCodes.join(', '),
    services_selected: serviceNames.join(', '),
    service_codes: serviceCodes.join(', '),
    service_names: serviceNames.join(', '),
    service_json: safeJson(selectedCategories),

    geo_comment: _.get(payload, 'geoComment')
      || _.get(payload, 'geo_comment')
      || '',
    message: comment,
    pd_consent: '1',

    name: _.get(payload, 'name') || '',
    fio: _.get(payload, 'name') || '',

    phone_country_iso: _.get(payload, 'phoneCountryIso') || '',
    phone_country_name: _.get(payload, 'phoneCountryName') || '',
    phone_dial_code: _.get(payload, 'phoneDialCode') || '',

    inn: _.get(payload, 'inn') || '',
    legal_type: _.get(legalType, 'value') || '',
    legal_type_label: _.get(legalType, 'label') || '',

    contact_name: _.get(payload, 'contactName') || '',
    contact_phone: _.get(payload, 'contactPhone') || '',

    country_id: _.get(country, 'id') || '',
    country_name: _.get(country, 'name') || '',

    region_id: regionId,
    region_name: regionName,

    service_category_ids: serviceIds.join(', '),
    service_category_names: serviceNames.join(', '),
    service_category_codes: serviceCodes.join(', '),

    comment,
    registration_date: _.get(payload, 'registrationDate') || '',

    page_url: metadata.page_url,
    user_agent: metadata.user_agent,
    referrer: metadata.referrer,
    first_referrer: metadata.first_referrer,
    first_landing_url: metadata.first_landing_url,

    volunteer_help_selected: volunteerMeta.volunteerSelected ? '1' : '0',
    volunteer_only: volunteerMeta.volunteerOnly ? '1' : '0',
    expectations_notice_shown: '1',
    reserve_status: _.get(payload, 'demandReserveStatus') || (_.get(demandOverall, 'reserve_status') ? '1' : '0'),
    connection_mode: _.get(payload, 'applicationSubmissionMode')
      || _.get(payload, 'demandConnectionMode')
      || _.get(demandOverall, 'connection_mode')
      || '',
    inform_network_help: 'shown',
    demand_signal: _.get(payload, 'demandSignal') || _.get(demandOverall, 'signal') || '',
    demand_recommendation: _.get(payload, 'demandRecommendation') || _.get(demandOverall, 'recommendation') || '',
    demand_comment: _.get(payload, 'demandComment') || _.get(demandOverall, 'comment') || '',
    demand_metrics_json: safeJson(_.get(demandStatus, 'rows') || _.get(payload, 'demandMetrics') || []),

    raw: safeJson(getSanitizedRawPayload(payload)),
  };
};

const normalizeGoogleSheetPayload = (data) => Object.keys(data || {}).reduce((result, key) => {
  const value = data[key];

  return {
    ...result,
    [key]: Array.isArray(value) ? value.join(', ') : safeString(value),
  };
}, {});

const postToGoogleSheetDirect = async (data) => {
  if (typeof window === 'undefined' || typeof URLSearchParams === 'undefined') {
    return Promise.resolve();
  }

  const params = new URLSearchParams();

  Object.keys(data || {}).forEach((key) => {
    const value = data[key];

    if (Array.isArray(value)) {
      value.forEach((item) => {
        params.append(key, safeString(item));
      });

      return;
    }

    params.append(key, safeString(value));
  });

  if (typeof fetch === 'function') {
    await fetch(GOOGLE_SHEETS_WEB_APP_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: params,
    });

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[iWALY] Google Sheets duplicate direct fallback request sent');
    }
  }

  return Promise.resolve();
};

const postToGoogleSheet = async (data) => {
  const normalized = normalizeGoogleSheetPayload(data);

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[iWALY] Google Sheets duplicate payload', normalized);
  }

  try {
    const response = await duplicateGoogleSheet(normalized);

    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[iWALY] Google Sheets duplicate backend response', response && response.data);
    }

    return response;
  } catch (error) {
    // Keep the old browser-side duplication as a temporary deployment fallback.
    // The backend endpoint is primary because it can log the real Apps Script response.
    // eslint-disable-next-line no-console
    console.warn('[iWALY] Google Sheets duplicate backend failed, using direct fallback', error);

    return postToGoogleSheetDirect(normalized);
  }
};

const duplicateOriginalSignupToGoogleSheet = async (payload, state) => {
  try {
    const data = buildGoogleSheetPayload(payload, state);

    await postToGoogleSheet(data);
  } catch (error) {
    // Google Sheets is only an additional copy.
    // It must not break the main backend registration.
    // eslint-disable-next-line no-console
    console.warn('[iWALY] Google Sheets duplicate failed', error);
  }
};

export default new Vuex.Store({
  state: {
    payload: {},
    code: null,
    countries: [],
    regions: {},
    serviceCategories: [],
    companies: [],
    legalTypes: [
      { value: 'llc', label: 'ООО', enabled: false },
      { value: 'sole', label: 'ИП', enabled: false },
      { value: 'individual', label: 'Самозанятый или ФЛ', enabled: true },
    ],
    step: 1,
  },
  getters: {
    regions: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.regions, id) || [];
      }

      return state.regions;
    },
    legalTypes: (state) => state.legalTypes.filter((type) => type.enabled),
  },
  actions: {
    ACT_SET_STATE: ({ commit }, payload) => {
      commit(SET_STATE, payload);
    },

    ACT_SET_STEP: ({ commit }, step) => {
      commit(SET_STEP, step);
    },

    ACT_FETCH_SERVICE_CATEGORIES: async ({ commit }) => serviceCategories()
      .then((response) => {
        if (response.data) {
          commit(SET_SERVICE_CATEGORIES, response.data);
        }
      }),

    ACT_FETCH_COUNTRIES: async ({ commit }) => countries()
      .then((response) => {
        if (response.data) {
          commit(SET_COUNTRIES, response.data);
        }
      }),

    ACT_FETCH_REGIONS: async ({
      commit,
      state,
    }, id) => {
      if (_.has(state.regions, id)) {
        return Promise.resolve(_.get(state.regions, id));
      }

      return regions(id).then((response) => {
        if (response.data) {
          commit(SET_REGIONS, {
            id,
            data: response.data,
          });

          return Promise.resolve(response.data);
        }

        return Promise.reject();
      });
    },

    ACT_REQUEST_CONFIRMATION_CODE: async ({ commit, state }) => {
      const phone = _.get(state, 'payload.phone', '').replace(/ /g, '');

      return requestConfirmationCode({ phone }).then((response) => {
        if (response.status === 200 && state.step === 1) {
          commit(SET_STEP, state.step + 1);
        }
      });
    },

    ACT_VERIFY_MOBILE_ID: async ({ commit, state }, payload) => {
      const phone = _.get(state, 'payload.phone', '').replace(/ /g, '');

      return verifyMobileId({
        phone,
        session_id: payload.session_id,
        verify_token: payload.verify_token,
      }).then((response) => {
        if (response.status === 200 && response.data && response.data.ok === true) {
          commit(SET_STATE, {
            code: '',
            mobileIdVerified: true,
            mobileIdSessionId: payload.session_id,
          });

          return Promise.resolve(response);
        }

        return Promise.reject();
      });
    },

    ACT_CHECK_CODE: async ({ commit, dispatch, state }, code) => {
      const phone = _.get(state, 'payload.phone', '').replace(/ /g, '');

      return checkCode({ phone, code }).then((response) => {
        if (response.status === 200) {
          const { result } = response.data;

          if (result) {
            commit(SET_STATE, { code });

            return dispatch('ACT_REGISTRATION').then(() => {
              commit(SET_STEP, 3);

              return Promise.resolve();
            });
          }

          return Promise.reject();
        }

        return Promise.reject();
      });
    },

    ACT_GET_SUGGESTIONS: _.debounce(async ({
      commit,
    }, value) => {
      if (value) {
        return suggest(value).then((response) => {
          const data = response.data || [];

          commit(SET_COMPANIES, data);

          return Promise.resolve(data);
        });
      }

      return Promise.resolve([]);
    }, 600),

    ACT_SET_SUGGESTION: ({
      commit,
      state,
    }, data) => {
      if (data && !_.isEqual(data, {})) {
        commit(SET_STATE, {
          name: data.name,
          inn: data.inn,
          kpp: _.get(data.legalInfo, 'organization.kpp'),
          ogrn: _.get(data.legalInfo, 'organization.ogrn'),
          managerName: _.get(data.legalInfo, 'organization.managerName'),
          registrationDate: _.get(data.legalInfo, 'organization.registrationDate'),
          legalType: state.legalTypes.find((obj) => (
            obj.value === _.get(data.legalInfo, 'organization.businessType')
          )),
          company: data,
        });

        EventBus.$emit('sync', data);
      } else {
        EventBus.$emit('sync', null);
      }
    },

    ACT_SUBMIT_APPLICATION_REVIEW: async ({ commit, state }) => {
      const payload = {
        ...state.payload,
        applicationSubmissionMode: 'application_review_reserve',
        connectionMode: _.get(state, 'payload.demandConnectionMode') || 'application_review_reserve',
        reserveStatus: '1',
        expectationsNoticeShown: '1',
      };

      commit(SET_STATE, payload);

      await duplicateOriginalSignupToGoogleSheet(payload, {
        ...state,
        payload,
      });

      commit(SET_STEP, 3);

      return Promise.resolve();
    },

    ACT_REGISTRATION: async ({ state }) => {
      const { payload } = state;
      let { phone, contactPhone } = payload;

      if (payload && phone) {
        phone = phone.replace(/ /g, '');
      }

      if (payload && contactPhone) {
        contactPhone = contactPhone.replace(/ /g, '');
      }

      const data = {
        phone: phone || '',
        password: payload.password,
        code: payload.code || '',
        profile: {
          name: payload.name,
          inn: payload.inn,
          email: payload.email,
          region: _.get(payload, 'region.id'),
          cityId: _.get(payload, 'city.id') || null,
          city: _.get(payload, 'city.name') || _.get(payload, 'cityName') || '',
          comment: _.get(payload, 'comment') || '',
          serviceCategories: payload.serviceCategories,
        },
      };

      switch (_.get(payload, 'legalType.value')) {
        case 'individual':
          data.profile.legalInfo = {
            type: 'individual',
            individual: {
              // bankCard: bankCard,
            },
          };
          break;

        case 'sole':
          data.profile.legalInfo = {
            type: 'organization',
            organization: {
              bankCode: payload.bankCode,
              bankAccount: payload.bankAccount,
              address: payload.address,
              ogrn: payload.ogrn,
              registrationDate: payload.registrationDate,
              managerName: payload.managerName,
              taxSystem: _.get(payload, 'taxSystem.key'),
              businessType: 'sole',
              contactPhone,
              contactName: payload.contactName,
            },
          };
          break;

        case 'llc':
          data.profile.legalInfo = {
            type: 'organization',
            organization: {
              bankCode: payload.bankCode,
              bankAccount: payload.bankAccount,
              address: payload.address,
              kpp: payload.kpp,
              ogrn: payload.ogrn,
              registrationDate: payload.registrationDate,
              managerName: payload.managerName,
              taxSystem: _.get(payload, 'taxSystem.key'),
              businessType: 'llc',
              contactPhone,
              contactName: payload.contactName,
            },
          };
          break;

        default:
      }

      return register(data).then(async (response) => {
        if (response.status === 200) {
          await duplicateOriginalSignupToGoogleSheet(payload, state);

          return Promise.resolve();
        }

        return Promise.reject();
      });
    },

    ACT_VERIFY_EMAIL: async (context, token) => verify({ token })
      .then(() => Promise.resolve())
      .catch(() => Promise.reject()),

    ACT_ON_LEGAL_TYPE_CHANGE: ({ commit, state }) => {
      const { payload } = state;

      commit(SET_STATE, {
        ...payload,
        name: '',
        inn: '',
        company: null,
        registrationDate: '',
        ogrn: '',
        kpp: '',
        managerName: '',
      });

      EventBus.$emit('sync');
    },
  },
  mutations: {
    [SET_STATE](state, data) {
      state.payload = {
        ...state.payload,
        ...data,
      };
    },

    [SET_SERVICE_CATEGORIES](state, data) {
      state.serviceCategories = data;
    },

    [SET_COUNTRIES](state, data) {
      state.countries = data;
    },

    [SET_REGIONS](state, { id, data }) {
      Vue.set(state.regions, id, data);
    },

    [SET_COMPANIES](state, data) {
      state.companies = data;
    },

    [SET_STEP](state, step) {
      state.step = step;
    },
  },
});
